// Saved YouTube creators — mirrors the APK's CreatorStore. Persists locally
// only for now (Supabase sync can be added once a creators table exists).
// Channel IDs are the canonical "UC..."  YouTube identifiers; latest videos
// are fetched from the public Atom feed
// (https://www.youtube.com/feeds/videos.xml?channel_id=UC...), which needs
// no API key. Browsers block the cross-origin XML fetch, so we proxy
// through a tiny VPS endpoint (see /api/youtube/feed below).

export type Creator = {
  channelId: string;  // UCxxx...
  name: string;
  handle?: string;
  avatarUrl?: string;
  hidden?: boolean;   // default creator the user hid
};

const K = 'mystack:creators:v1';

function read(): Creator[] {
  try { return JSON.parse(localStorage.getItem(K) || '[]') as Creator[]; } catch { return []; }
}
function write(list: Creator[]): void {
  localStorage.setItem(K, JSON.stringify(list));
}

export function allCreators(): Creator[] { return read(); }

export function visibleCreators(): Creator[] { return read().filter((c) => !c.hidden); }

export function addCreator(c: Creator): void {
  const list = read();
  if (list.some((x) => x.channelId === c.channelId)) return;
  list.push(c);
  write(list);
}

export function removeCreator(channelId: string): void {
  write(read().filter((c) => c.channelId !== channelId));
}

export function setHidden(channelId: string, hidden: boolean): void {
  const list = read();
  const i = list.findIndex((c) => c.channelId === channelId);
  if (i >= 0) {
    list[i].hidden = hidden;
    write(list);
  }
}

// --- YouTube feed fetch ---

export type YoutubeVideo = {
  videoId: string;
  title: string;
  channelId: string;
  channelName: string;
  thumbnailUrl: string;
  publishedAt: string;
};

const CORS_PROXY = 'https://api.codetabs.com/v1/proxy?quest=';

/** Resolve a pasted URL/handle to channelId + name. */
export async function resolveChannel(input: string): Promise<Creator | null> {
  const trimmed = input.trim();
  // Already a UC... id?
  const ucMatch = trimmed.match(/(UC[A-Za-z0-9_-]{22})/);
  if (ucMatch) {
    const cid = ucMatch[1];
    return enrich(cid);
  }
  // Scrape the page for the embedded channelId.
  const url = trimmed.startsWith('http') ? trimmed : `https://www.youtube.com/${trimmed.replace(/^\/+/, '')}`;
  try {
    const r = await fetch(CORS_PROXY + encodeURIComponent(url));
    if (!r.ok) return null;
    const html = await r.text();
    const m = html.match(/"channelId":"(UC[A-Za-z0-9_-]{22})"/);
    if (!m) return null;
    return enrich(m[1]);
  } catch { return null; }
}

async function enrich(channelId: string): Promise<Creator> {
  try {
    const url = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
    const r = await fetch(CORS_PROXY + encodeURIComponent(url));
    if (r.ok) {
      const xml = await r.text();
      const nameMatch = xml.match(/<title>([^<]+)<\/title>/);
      return { channelId, name: nameMatch?.[1] ?? 'YouTube channel' };
    }
  } catch {/* fallthrough */}
  return { channelId, name: 'YouTube channel' };
}

export async function latestVideos(channelId: string, channelName?: string): Promise<YoutubeVideo[]> {
  const url = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
  try {
    const r = await fetch(CORS_PROXY + encodeURIComponent(url));
    if (!r.ok) return [];
    const xml = await r.text();
    return parseAtom(xml, channelId, channelName);
  } catch { return []; }
}

function parseAtom(xml: string, channelId: string, fallbackName?: string): YoutubeVideo[] {
  const out: YoutubeVideo[] = [];
  const entryRe = /<entry>([\s\S]*?)<\/entry>/g;
  const feedNameMatch = xml.match(/<author>[\s\S]*?<name>([^<]+)<\/name>/);
  const feedName = feedNameMatch?.[1] ?? fallbackName ?? '';
  let m: RegExpExecArray | null;
  while ((m = entryRe.exec(xml))) {
    const entry = m[1];
    const vid = entry.match(/<yt:videoId>([^<]+)<\/yt:videoId>/)?.[1] ?? '';
    const title = entry.match(/<title>([^<]+)<\/title>/)?.[1] ?? '';
    const published = entry.match(/<published>([^<]+)<\/published>/)?.[1] ?? '';
    if (!vid) continue;
    out.push({
      videoId: vid,
      title: unescape(title),
      channelId,
      channelName: feedName,
      thumbnailUrl: `https://i.ytimg.com/vi/${vid}/mqdefault.jpg`,
      publishedAt: published,
    });
  }
  return out;
}

function unescape(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
